import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "@majourneys/icons";
interface UseShelfScrollOptions {
  /** How far one arrow press travels, in px. Roughly one card plus its gap. */
  step?: number;
}

export interface ShelfScroll {
  ref: React.RefObject<HTMLDivElement | null>;
  canScrollBack: boolean;
  canScrollForward: boolean;
  scrollBack: () => void;
  scrollForward: () => void;
}

/**
 * Scroll state for a horizontal shelf.
 *
 * Our rows run off the edge of the viewport with nothing to say so, so anyone
 * who does not think to swipe never sees past the third card. That is not a
 * polish problem: it is inventory we paid to publish being invisible.
 *
 * This is a hook rather than a wrapper component on purpose. The arrows have
 * to live in the section header, beside "See all", because anywhere inside the
 * row means sitting on top of the first card and hiding the thing the reader
 * came to look at. A wrapper cannot place them there without owning the header
 * too, so the caller places them and this owns only the measuring.
 *
 * An earlier revision also faded the leading edge with `mask-image`. Removed:
 * it read as a washed-out card rather than as a hint that the row continues.
 */
export function useShelfScroll({ step = 240 }: UseShelfScrollOptions = {}): ShelfScroll {
  const ref = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    /* `scrollLeft` is negative in a right-to-left container in some engines,
       so distance travelled is the magnitude, and the far edge is compared
       against the measured maximum rather than assumed to be zero. */
    const pos = Math.abs(el.scrollLeft);
    const start = pos > 4;
    const end = max > 4 && pos < max - 4;
    /* Runs on every scroll event: keep the same object when nothing changed,
       or each swipe re-renders the whole row of cards every frame. */
    setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    /* A row can gain or lose overflow long after mount: images settle, a
       translation rewraps a title, a filter changes the card count. */
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
      for (const child of Array.from(el.children)) ro.observe(child);
    }
    return () => {
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [measure]);

  const nudge = useCallback(
    (towardEnd: boolean) => {
      const el = ref.current;
      if (!el) return;
      const rtl = getComputedStyle(el).direction === "rtl";
      /* In a right-to-left row, moving toward the end means decreasing
         scrollLeft, so the sign follows the writing direction. */
      const sign = towardEnd === rtl ? -1 : 1;
      el.scrollBy({ left: sign * step, behavior: "smooth" });
    },
    [step],
  );

  return {
    ref,
    canScrollBack: edges.start,
    canScrollForward: edges.end,
    scrollBack: () => nudge(false),
    scrollForward: () => nudge(true),
  };
}

/**
 * The paired arrows for a shelf, meant for a section header.
 *
 * Renders nothing at all when the row fits, so a short row does not carry two
 * dead controls. Hidden below `sm`, where a swipe is the natural gesture and
 * an arrow is only something to hit by accident.
 */
export function ShelfArrows({ shelf }: { shelf: ShelfScroll }) {
  const { t, i18n } = useTranslation();
  /* Logical properties would place a button, but a glyph does not flip on its
     own. The codebase swaps the icon off the language rather than using an
     `rtl:` variant, so this follows that. */
  const rtlLang = i18n.language === "ar";

  if (!shelf.canScrollBack && !shelf.canScrollForward) return null;

  return (
    <span className="hidden items-center gap-1.5 sm:inline-flex">
      <ShelfArrow
        label={t("common.scroll_back", { defaultValue: "Scroll back" })}
        icon={rtlLang ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"}
        disabled={!shelf.canScrollBack}
        onClick={shelf.scrollBack}
      />
      <ShelfArrow
        label={t("common.scroll_forward", { defaultValue: "Scroll forward" })}
        icon={rtlLang ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line"}
        disabled={!shelf.canScrollForward}
        onClick={shelf.scrollForward}
      />
    </span>
  );
}

/**
 * One round shelf arrow. Exported so other sideways rows (the category type
 * chips) use the same control rather than a look of their own. `className`
 * carries the display (`grid` by default), so a caller can hide it on phones.
 */
export function ShelfArrow({
  label,
  icon,
  disabled = false,
  onClick,
  className = "grid",
}: {
  label: string;
  icon: string;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      /* Dimmed rather than removed at the ends, so the pair does not reflow
         and move the other arrow out from under the cursor. */
      className={`h-8 w-8 place-items-center rounded-full border border-[rgba(4,0,51,0.10)] bg-white transition ${
        disabled
          ? "cursor-not-allowed opacity-35"
          : "hover:border-[rgba(4,0,51,0.24)]"
      } ${className}`}
    >
      <Icon name={icon} className={`text-[17px] text-[#110E39]`} aria-hidden />
    </button>
  );
}
